import { Directive, ElementRef, Input, OnDestroy, OnInit, inject } from '@angular/core';
import { TourService, type IStepOption, type TourAnchorDirective } from 'ngx-ui-tour-core';
import { KirigamiTourStepHostService } from './tour-step-host.service';

/**
 * Marks a DOM element as a tour target. Implements ngx-ui-tour's
 * `TourAnchorDirective` contract: the `TourService` calls `showTourStep` /
 * `hideTourStep` on the registered anchor whose id matches the active step.
 * We forward those to the shared host service, which drives the popover.
 */
@Directive({
  selector: '[kirigamiTourAnchor]',
  standalone: true,
})
export class KirigamiTourAnchorDirective implements TourAnchorDirective, OnInit, OnDestroy {
  /** The anchor id referenced by a step's `anchorId`. */
  @Input('kirigamiTourAnchor') anchorId!: string;

  readonly element = inject(ElementRef<HTMLElement>);
  // Optional: the engine is only registered when an app calls
  // provideKirigamiTour(). Absent (e.g. in component unit tests), the anchor
  // is simply inert rather than throwing a missing-provider error.
  private readonly tourService = inject(TourService, { optional: true });
  private readonly host = inject(KirigamiTourStepHostService);

  ngOnInit(): void {
    this.tourService?.register(this.anchorId, this);
  }

  ngOnDestroy(): void {
    this.tourService?.unregister(this.anchorId);
  }

  showTourStep(step: IStepOption): void {
    this.host.show(this.element.nativeElement, step);
  }

  hideTourStep(): void {
    this.host.hide();
  }
}
